import React, { useState, useEffect } from "react";
import axios from "axios";
import { ArrowRight, CaretDown, Check, CircleNotch, DownloadSimple, FolderOpen, Sparkle, Upload, VideoCamera } from "@phosphor-icons/react";
import { ChipSelector } from "@/components/shared";
import { API, BACKEND_URL } from "@/lib/api";

// ============= LIFESTYLE VIDEO SECTION =============
// Pre-loaded lifestyle topics
const LifestyleVideo = () => {
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [outfitFile, setOutfitFile] = useState(null);
  const [outfitPreview, setOutfitPreview] = useState(null);
  const [outfitChangedUrl, setOutfitChangedUrl] = useState(null);
  const [changingOutfit, setChangingOutfit] = useState(false);
  const [vibe, setVibe] = useState("soft-life");
  const [topic, setTopic] = useState("");
  const [style, setStyle] = useState("confident");
  const [model, setModel] = useState("seedance-2-vip-image-to-video-fast");
  const [preserveFace, setPreserveFace] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [currentJob, setCurrentJob] = useState(null);
  const [pastJobs, setPastJobs] = useState([]);
  const [expandedJob, setExpandedJob] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const vibes = [
    { value: "boss", label: "Boss Mode" },
    { value: "morning-routine", label: "Morning Routine" },
    { value: "soft-life", label: "Soft Life" },
    { value: "hustle", label: "Hustle" },
    { value: "travel", label: "Travel" },
    { value: "creative", label: "Creative" },
    { value: "celebration", label: "Celebration" },
    { value: "fitness", label: "Fitness" },
    { value: "professional", label: "Professional" },
  ];
  const styles = [
    { value: "confident", label: "Confident & Polished" },
    { value: "candid", label: "Candid & Real" },
    { value: "luxury", label: "Luxury" },
    { value: "editorial", label: "Editorial" },
    { value: "street", label: "Street Style" },
    { value: "corporate", label: "Corporate" },
  ];
  const models = [
    { value: "seedance-2-vip-image-to-video-fast", label: "Seedance 2.0 VIP Fast" },
    { value: "seedance-2-image-to-video-fast", label: "Seedance 2.0 Fast" },
    { value: "seedance-2-image-to-video", label: "Seedance 2.0 Pro" },
    { value: "veo3-image-to-video", label: "Veo3" },
    { value: "veo3-fast-image-to-video", label: "Veo3 Fast" },
    { value: "kling-v3.0-pro-image-to-video", label: "Kling 3.0 Pro" },
    { value: "kling-v3.0-standard-image-to-video", label: "Kling 3.0 Std" },
    { value: "wan2.6-image-to-video", label: "WAN 2.6" },
    { value: "pixverse-v6-i2v", label: "PixVerse v6" },
    { value: "runway-image-to-video", label: "Runway" },
  ];

  useEffect(() => {
    axios.get(`${API}/ai-twin/images`).then((r) => setImages(r.data.images || []));
    axios.get(`${API}/lifestyle/jobs`).then((r) => setPastJobs(r.data.jobs || []));
  }, []);

  const handleOutfitDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0] || e.target?.files?.[0];
    if (file && file.type.startsWith("image/")) {
      setOutfitFile(file);
      setOutfitPreview(URL.createObjectURL(file));
      setOutfitChangedUrl(null);
    }
  };

  const changeOutfit = async () => {
    if (!selectedImage || !outfitFile) return;
    setChangingOutfit(true);
    try {
      // Upload outfit image first
      const formData = new FormData();
      formData.append("file", outfitFile);
      const uploadRes = await axios.post(`${API}/lifestyle/upload-outfit`, formData);
      const garmentUrl = uploadRes.data.url;

      // Call dress change
      const res = await axios.post(`${API}/lifestyle/outfit-change?model_image_id=${selectedImage.id}&garment_url=${encodeURIComponent(garmentUrl)}`);
      setOutfitChangedUrl(res.data.image_url);
    } catch (e) {
      console.error("Outfit change error:", e);
      alert("Outfit change failed. Try a different image.");
    } finally {
      setChangingOutfit(false);
    }
  };

  const generateVideo = async () => {
    if (!selectedImage) return;
    setGenerating(true);
    setCurrentJob(null);
    try {
      let finalOutfitUrl = outfitChangedUrl;
      
      // Auto-apply outfit if file uploaded but not yet applied
      if (outfitFile && !outfitChangedUrl) {
        try {
          const formData = new FormData();
          formData.append("file", outfitFile);
          const uploadRes = await axios.post(`${API}/lifestyle/upload-outfit`, formData);
          const garmentUrl = uploadRes.data.url;
          const dressRes = await axios.post(`${API}/lifestyle/outfit-change?model_image_id=${selectedImage.id}&garment_url=${encodeURIComponent(garmentUrl)}`);
          finalOutfitUrl = dressRes.data.image_url;
          setOutfitChangedUrl(finalOutfitUrl);
        } catch (e) {
          console.error("Auto outfit change failed:", e);
          alert("Outfit change failed — generating with original image instead.");
          finalOutfitUrl = null;
        }
      }

      const res = await axios.post(`${API}/lifestyle/generate-video`, {
        image_id: selectedImage.id,
        outfit_image_url: finalOutfitUrl || null,
        vibe,
        topic,
        style,
        model,
        preserve_face: preserveFace,
      });
      const jobId = res.data.job_id;
      setCurrentJob({ id: jobId, status: "processing" });

      // Poll for completion
      const pollInterval = setInterval(async () => {
        try {
          const pollRes = await axios.get(`${API}/lifestyle/jobs/${jobId}`);
          const job = pollRes.data;
          setCurrentJob(job);
          if (job.status === "completed" || job.status === "failed") {
            clearInterval(pollInterval);
            setGenerating(false);
            setPastJobs((prev) => [job, ...prev.filter((j) => j.id !== job.id)]);
          }
        } catch (err) {
          clearInterval(pollInterval);
          setGenerating(false);
        }
      }, 5000);
    } catch (e) {
      console.error("Generate error:", e);
      setGenerating(false);
    }
  };

  const saveToVault = async (job) => {
    try {
      await axios.post(`${API}/vault/save`, {
        title: job.topic || job.vibe,
        platform: "tiktok",
        category: "lifestyle",
        video_url: job.video_url,
        caption: "",
        hashtags: "",
        tags: job.vibe,
        post_type: "reel",
        source_job_id: job.id,
      });
      alert("Saved to Content Vault!");
    } catch (e) {
      alert("Error saving to vault");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold text-zinc-900">Lifestyle Video Creator</h2>
        <p className="text-sm text-zinc-500 mt-1">Generate AI Twin lifestyle videos with outfit changes</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Settings */}
        <div className="space-y-4">
          {/* Step 1: Select AI Twin */}
          <div className="card p-4 sm:p-6 space-y-3">
            <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">1. Select AI Twin</p>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {images.map((img) => (
                <div
                  key={img.id}
                  onClick={() => { setSelectedImage(img); setOutfitChangedUrl(null); }}
                  className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all aspect-square ${
                    selectedImage?.id === img.id ? "border-[#E91E8C] ring-2 ring-[#E91E8C]/30" : "border-zinc-200 hover:border-zinc-300"
                  }`}
                  data-testid={`lifestyle-twin-${img.id}`}
                >
                  <img src={`${BACKEND_URL}/api/ai-twin/images/${img.filename}`} alt="" className="w-full h-full object-cover" />
                  {selectedImage?.id === img.id && (
                    <div className="absolute inset-0 bg-[#E91E8C]/20 flex items-center justify-center">
                      <Check size={20} className="text-white" weight="bold" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Step 2: Outfit Change (Optional) */}
          <div className="card p-4 sm:p-6 space-y-3">
            <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">2. Change Outfit (Optional)</p>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleOutfitDrop}
              className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all ${
                dragOver ? "border-[#E91E8C] bg-[#E91E8C]/5" : "border-zinc-200 hover:border-zinc-300"
              }`}
              data-testid="outfit-dropzone"
            >
              <input
                type="file"
                accept="image/*"
                onChange={handleOutfitDrop}
                className="hidden"
                id="outfit-upload"
              />
              <label htmlFor="outfit-upload" className="cursor-pointer">
                {outfitPreview ? (
                  <div className="flex items-center gap-3 justify-center">
                    <img src={outfitPreview} alt="Outfit" className="w-16 h-16 object-cover rounded-lg" />
                    <div className="text-left">
                      <p className="text-sm font-medium text-zinc-700">Outfit uploaded</p>
                      <p className="text-xs text-zinc-400">Drop a new image to replace</p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <Upload size={24} className="mx-auto text-zinc-300 mb-2" />
                    <p className="text-sm text-zinc-500">Drag & drop an outfit image here</p>
                    <p className="text-xs text-zinc-400 mt-1">or click to browse</p>
                  </div>
                )}
              </label>
            </div>
            {outfitFile && selectedImage && !outfitChangedUrl && (
              <button
                onClick={changeOutfit}
                disabled={changingOutfit}
                className="btn-secondary w-full text-sm"
                data-testid="apply-outfit-btn"
              >
                {changingOutfit ? "Applying full outfit (2 passes)..." : "Apply Full Outfit to AI Twin"}
              </button>
            )}
            {outfitChangedUrl && (
              <div className="space-y-2">
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2">
                    <div className="text-center">
                      <img src={`${BACKEND_URL}/api/ai-twin/images/${selectedImage?.filename}`} alt="Before" className="w-20 h-20 object-cover rounded-lg border border-zinc-200" />
                      <p className="text-[10px] text-zinc-400 mt-1">Before</p>
                    </div>
                    <ArrowRight size={16} className="text-green-500" />
                    <div className="text-center">
                      <img src={outfitChangedUrl} alt="After" className="w-20 h-20 object-cover rounded-lg border-2 border-green-400" />
                      <p className="text-[10px] text-green-600 font-medium mt-1">After</p>
                    </div>
                  </div>
                  <div className="flex-1 ml-2">
                    <p className="text-sm font-medium text-green-700">Outfit applied!</p>
                    <p className="text-xs text-green-600">This image will be used for video</p>
                  </div>
                </div>
                <button onClick={() => { setOutfitChangedUrl(null); setOutfitFile(null); setOutfitPreview(null); }} className="text-xs text-zinc-400 underline">Remove outfit change</button>
              </div>
            )}
          </div>

          {/* Step 3: Video Settings */}
          <div className="card p-4 sm:p-6 space-y-3">
            <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">3. Video Settings</p>
            <ChipSelector label="Vibe" options={vibes} selected={vibe} onChange={setVibe} />
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-2">Topic (Optional)</label>
              <input
                type="text"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="e.g. Morning coffee ritual, Boss moment at the office..."
                className="input-field text-sm"
                data-testid="lifestyle-topic-input"
              />
            </div>
            <ChipSelector label="Style" options={styles} selected={style} onChange={setStyle} />
            <ChipSelector label="Model" options={models} selected={model} onChange={setModel} />

            {/* Face preservation toggle (Magic Hour post-process) */}
            <label
              className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                preserveFace
                  ? "border-[#E91E8C]/40 bg-gradient-to-br from-[#E91E8C]/5 to-pink-50/50"
                  : "border-zinc-200 bg-white hover:border-zinc-300"
              }`}
              data-testid="preserve-face-toggle"
            >
              <input
                type="checkbox"
                checked={preserveFace}
                onChange={(e) => setPreserveFace(e.target.checked)}
                className="mt-0.5 accent-[#E91E8C]"
                data-testid="preserve-face-checkbox"
              />
              <div className="flex-1">
                <div className="flex items-center gap-1.5">
                  <Sparkle size={14} weight="fill" className="text-[#E91E8C]" />
                  <span className="text-sm font-bold text-zinc-900">Preserve my exact face (recommended)</span>
                </div>
                <p className="text-[11px] text-zinc-500 leading-relaxed mt-0.5">
                  After the scene renders, we'll use Magic Hour Face Swap to stamp your real face onto every frame — so you don't drift into a stranger. Adds ~2-4 min. Requires your Magic Hour API key in Settings.
                </p>
              </div>
            </label>
          </div>

          {/* Generate Button */}
          <button
            onClick={generateVideo}
            disabled={generating || !selectedImage}
            className="btn-primary w-full py-3 text-sm font-semibold flex items-center justify-center gap-2"
            data-testid="generate-lifestyle-video-btn"
          >
            {generating ? (
              <><CircleNotch size={18} className="animate-spin" /> {outfitFile && !outfitChangedUrl ? "Applying Full Outfit & Generating..." : "Generating Video..."}</>
            ) : (
              <><VideoCamera size={18} /> {outfitFile && !outfitChangedUrl ? "Apply Full Outfit & Generate" : "Generate Lifestyle Video"}</>
            )}
          </button>
        </div>

        {/* Right Column - Results & History */}
        <div className="space-y-4">
          {/* Current Job Progress */}
          {currentJob && (
            <div className="card p-4 sm:p-6 space-y-3" data-testid="lifestyle-current-job">
              <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">Current Generation</p>
              {currentJob.status === "processing" && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <CircleNotch size={16} className="animate-spin text-[#E91E8C]" />
                    <span className="text-sm text-zinc-600">
                      {currentJob.current_step === "face_preservation" ? (
                        <>Preserving your face with Magic Hour (~2-4 min)...</>
                      ) : (
                        <>Generating video with {models.find(m => m.value === model)?.label || model}...</>
                      )}
                    </span>
                  </div>
                  <div className="w-full h-2 bg-zinc-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-[#E91E8C] to-[#ff6ec7] animate-pulse" style={{ width: currentJob.current_step === "face_preservation" ? "85%" : "60%" }} />
                  </div>
                </div>
              )}
              {currentJob.status === "completed" && currentJob.video_url && (
                <div className="space-y-3">
                  {currentJob.face_swap_status === "completed" && (
                    <div className="flex items-center gap-1.5 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="face-swap-applied-badge">
                      <Check size={13} weight="bold" className="text-emerald-600" />
                      <span className="text-[11px] font-semibold text-emerald-800">Face preserved with Magic Hour</span>
                    </div>
                  )}
                  {currentJob.face_swap_status === "no_magic_hour_key" && (
                    <div className="flex items-start gap-1.5 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg" data-testid="face-swap-skipped-badge">
                      <span className="text-[11px] text-amber-800">
                        ⚠️ Face preservation skipped — add your Magic Hour API key in <strong>Account Settings → BYOK</strong> to stamp your real face onto every frame next time.
                      </span>
                    </div>
                  )}
                  {typeof currentJob.face_swap_status === "string" && currentJob.face_swap_status.startsWith("failed") && (
                    <div className="flex items-start gap-1.5 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                      <span className="text-[11px] text-amber-800">
                        ⚠️ Face preservation didn't complete. Returning the base video — your face may drift. Try again or check your Magic Hour credits.
                      </span>
                    </div>
                  )}
                  <div className="rounded-lg overflow-hidden bg-black aspect-[9/16] max-h-[400px] mx-auto">
                    <video src={currentJob.video_url} controls className="w-full h-full object-contain" />
                  </div>
                  <div className="flex gap-2">
                    <a href={currentJob.video_url} download className="btn-primary flex-1 text-sm text-center flex items-center justify-center gap-1">
                      <DownloadSimple size={16} /> Download
                    </a>
                    <button onClick={() => saveToVault(currentJob)} className="btn-secondary flex-1 text-sm flex items-center justify-center gap-1">
                      <FolderOpen size={16} /> Save to Vault
                    </button>
                  </div>
                </div>
              )}
              {currentJob.status === "failed" && (
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm text-red-600">Generation failed: {currentJob.error || "Unknown error"}</p>
                </div>
              )}
            </div>
          )}

          {/* Past Lifestyle Videos */}
          <div className="card p-4 sm:p-6 space-y-3">
            <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">Past Lifestyle Videos</p>
            {pastJobs.length === 0 ? (
              <p className="text-sm text-zinc-400 text-center py-4">No lifestyle videos yet. Generate your first one!</p>
            ) : (
              <div className="space-y-2">
                {pastJobs.map((job) => (
                  <div key={job.id} className="border border-zinc-100 rounded-lg overflow-hidden" data-testid={`lifestyle-job-${job.id}`}>
                    <div
                      className="flex items-center justify-between p-3 cursor-pointer hover:bg-zinc-50"
                      onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-zinc-900 truncate">{job.topic || job.vibe}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <p className="text-xs text-zinc-400">
                            {new Date(job.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                          </p>
                          <span className={`text-xs font-medium ${
                            job.status === "completed" ? "text-green-600" : job.status === "failed" ? "text-red-600" : "text-amber-600"
                          }`}>{job.status === "completed" ? "Done" : job.status === "failed" ? "Failed" : "Processing"}</span>
                        </div>
                      </div>
                      <CaretDown size={14} className={`text-zinc-400 transition-transform ${expandedJob === job.id ? "rotate-180" : ""}`} />
                    </div>
                    {expandedJob === job.id && (
                      <div className="border-t border-zinc-100 p-3 space-y-3">
                        {job.status === "completed" && job.video_url && (
                          <>
                            <div className="rounded-lg overflow-hidden bg-black aspect-[9/16] max-h-[300px] mx-auto">
                              <video src={job.video_url} controls className="w-full h-full object-contain" />
                            </div>
                            <div className="flex gap-2">
                              <a href={job.video_url} download className="btn-primary flex-1 text-xs text-center flex items-center justify-center gap-1">
                                <DownloadSimple size={14} /> Download
                              </a>
                              <button onClick={() => saveToVault(job)} className="btn-secondary flex-1 text-xs flex items-center justify-center gap-1">
                                <FolderOpen size={14} /> Save to Vault
                              </button>
                            </div>
                          </>
                        )}
                        {job.status === "failed" && (
                          <>
                            <p className="text-xs text-red-500">{job.error}</p>
                            <button
                              onClick={async () => {
                                try {
                                  await axios.post(`${API}/lifestyle/jobs/${job.id}/retry`);
                                  const r = await axios.get(`${API}/lifestyle/jobs`);
                                  setPastJobs(r.data.jobs || []);
                                } catch (e) {
                                  alert(e.response?.data?.detail || "Retry failed");
                                }
                              }}
                              className="mt-1 text-xs font-semibold text-[#E91E8C] hover:text-[#c91a7b] border border-[#E91E8C] rounded px-2 py-1 inline-flex items-center gap-1"
                              data-testid={`lifestyle-retry-${job.id}`}
                            >
                              ↻ Retry
                            </button>
                          </>
                        )}
                        <p className="text-xs text-zinc-400">Model: {job.model} | Vibe: {job.vibe} | Style: {job.style}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LifestyleVideo;
